import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SettingOutlined, LockOutlined, TeamOutlined } from '@ant-design/icons';
import { useChannelStore, useDmStore, usePresenceStore, useWsStore, useMessageStore, useAuthStore } from '../stores';
import { useTaskStore } from '../stores/useTaskStore';
import { leaveChannel, joinChannel, listCommands } from '../lib/api';
import { routes } from '../lib/routes';
import { useGuildId } from '../hooks/useGuildId';
import { channelDraftKey } from '../hooks/useComposerDraft';
import { commandRegistry } from '../commands/registry';
import type { RemoteCommand } from '../commands/registry';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ActivityBar from './ActivityBar';
import ConnectionStatus from './ConnectionStatus';
import ServerMembersPanel from './ServerMembersPanel';
import ChannelSettingsModal from './ChannelSettingsModal';
import FilesPanel from './FilesPanel';
import TasksPanel from './TasksPanel';
import InsightsPanel from './InsightsPanel';
import { useVisualViewport } from '../hooks/useVisualViewport';
import { canLeaveChannel } from '../lib/channelManagement';
import { channelDisplayName } from '../lib/channelDisplay';
import { ExecutionUsageChip } from './ExecutionUsageChip';

interface Props {
  channelId: string;
  hideHeader?: boolean;
  onThreadOpen?: (messageId: string) => void;
  activeThreadMessageId?: string;
  composerDisabled?: boolean;
  onTaskClick?: (task: { thread_id?: string | null }) => void;
}

// Doubles as the `?tab=` deep-link allowlist. There is no server-side default;
// chat is the tab a channel opens on.
const TAB_KEYS = ['chat', 'files', 'tasks', 'insights'] as const;
type TabKey = (typeof TAB_KEYS)[number];

function tabFromSearch(search: string): TabKey {
  const params = new URLSearchParams(search);
  const raw = params.get('tab');
  // `raw` is an untrusted string off the URL, so it is checked against the tab
  // set rather than cast.
  if (raw && (TAB_KEYS as readonly string[]).includes(raw)) {
    return raw as TabKey;
  }
  return 'chat';
}

function searchWithTab(search: string, tab: TabKey): string {
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return `?${params.toString()}`;
}

export default function ChannelView({ channelId, hideHeader, onThreadOpen, activeThreadMessageId, composerDisabled = false, onTaskClick }: Props) {
  const channels = useChannelStore(s => s.channels);
  const dmChannels = useDmStore(s => s.dmChannels);
  const connectionState = usePresenceStore(s => s.connectionState);
  const sendWsMessage = useWsStore(s => s.sendWsMessage);
  const loadMessages = useMessageStore(s => s.loadMessages);
  const loadChannels = useChannelStore(s => s.loadChannels);
  const currentUser = useAuthStore(s => s.currentUser);
  const navigate = useNavigate();
  const location = useLocation();
  const guildId = useGuildId();
  const [showMembers, setShowMembers] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [joining, setJoining] = useState(false);
  const [starterPrompt, setStarterPrompt] = useState<string | undefined>();
  // The selected surface belongs to the route. Deriving it on every render
  // avoids a second, effect-synchronized tab state that can lag a Link which
  // changes only ?tab= while leaving the channel route intact.
  const activeTab = tabFromSearch(location.search);
  const keyboardHeight = useVisualViewport();

  const channel = channels.find(c => c.id === channelId);
  const dmChannel = dmChannels.find(dm => dm.id === channelId);
  const isDm = !!dmChannel?.peer;
  const isMember = channel?.is_member !== false;
  const canLeaveCurrentChannel = channel ? canLeaveChannel(channel, currentUser?.id) : false;
  const isReadOnlyPublicChannel = !isDm && channel && !isMember && channel.visibility === 'public';

  // Task cards are part of a public Channel's readable surface. Load the
  // task-scoped assignee profile independently of the member directory so
  // read-only visitors see the same task identity without gaining membership.
  useEffect(() => {
    if (!isDm) void useTaskStore.getState().loadChannelTasks(channelId).catch(() => {});
  }, [channelId, isDm]);

  useEffect(() => {
    void loadMessages(channelId);
  }, [channelId, loadMessages]);

  // Stamp the default Chat tab onto a channel route that omitted it. Route
  // transitions use React Router rather than window.history so every location
  // consumer observes the same change.
  useEffect(() => {
    if (!isDm && !new URLSearchParams(location.search).has('tab')) {
      navigate({ search: searchWithTab(location.search, 'chat') }, { replace: true });
    }
  }, [location.search, isDm, navigate]);

  const handleTabChange = useCallback((tab: TabKey) => {
    navigate({ search: searchWithTab(location.search, tab) });
  }, [location.search, navigate]);

  // Load slash commands on mount / channelId change + WS live-reload
  useEffect(() => {
    let cancelled = false;

    const loadCommands = async () => {
      try {
        const data = await listCommands(channelId);
        if (cancelled) return;
        const remoteCommands: RemoteCommand[] = data.agent.flatMap(a =>
          a.commands.map(c => ({ ...c, agentId: a.agent_id, agentName: a.agent_name }))
        );
        commandRegistry.setRemoteCommands(remoteCommands);
      } catch (err) {
        console.warn('[commands] Failed to load commands:', err);
      }
    };

    if (!isReadOnlyPublicChannel) loadCommands();

    // Debounce WS commands_updated events (300ms) to avoid broadcast storms
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    const handleCommandsUpdated = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadCommands();
      }, 300);
    };

    window.addEventListener('commands_updated', handleCommandsUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener('commands_updated', handleCommandsUpdated);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [channelId, isReadOnlyPublicChannel]);

  if (!channel && !isDm) {
    // A forbidden channel/DM (not in the membership-scoped state lists) and a
    // genuinely non-existent id BOTH land here. Conflating forbidden with
    // not-found is the secure choice — the message must NOT reveal whether the
    // resource exists (no existence leak). `data-channel-not-found` is a stable
    // testability marker so the access-denied state is assertable in e2e; it
    // does not change the (intentionally indistinguishable) user-facing copy.
    return (
      <div className="channel-view" style={keyboardHeight > 0 ? { height: `calc(100% - ${keyboardHeight}px)` } : undefined}>
        <div className="channel-empty" data-channel-not-found="true" role="status">
          Channel not found
        </div>
      </div>
    );
  }

  const headerTitle = isDm ? dmChannel.peer.display_name : '';
  const headerTopic = isDm ? undefined : channel!.topic;
  const isGeneral = channel?.name === 'general';

  const handleLeave = async () => {
    try {
      await leaveChannel(channelId);
      await loadChannels();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to leave');
    }
  };

  const handleJoin = async () => {
    if (joining) return;
    setJoining(true);
    try {
      await joinChannel(channelId);
      await loadChannels();
      await loadMessages(channelId);
      sendWsMessage({ type: 'subscribe', channel_id: channelId });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to join');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="channel-view" data-channel-type={isDm ? 'dm' : 'channel'} style={keyboardHeight > 0 ? { height: `calc(100% - ${keyboardHeight}px)` } : undefined}>
      {!hideHeader && (
      <div className="channel-header">
        <div className="channel-header-info">
          <h2 className="channel-title">
            {isDm ? headerTitle : (
              <>{channel!.visibility === 'private' ? <LockOutlined style={{ marginRight: 6 }} /> : '# '}{channelDisplayName(channel!)}</>
            )}
          </h2>
          {headerTopic && <span className="channel-topic">{headerTopic}</span>}
        </div>
        {!isDm && channel && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExecutionUsageChip resourceId={channelId} scope="channel" />
            {canLeaveCurrentChannel && (
              <button
                className="btn btn-sm leave-btn"
                title="Leave channel"
                onClick={handleLeave}
              >
                Leave
              </button>
            )}
            <button
              className="icon-btn"
              title="Channel Settings"
              onClick={() => setShowSettings(true)}
            >
              <SettingOutlined />
            </button>
            <button
              className={`icon-btn${showMembers ? ' icon-btn-active' : ''}`}
              title="Members"
              onClick={() => setShowMembers(s => !s)}
            >
              <TeamOutlined />{channel!.member_count != null && <span style={{ marginLeft: 4, fontSize: 13 }}>{channel!.member_count}</span>}
            </button>
          </div>
        )}
      </div>
      )}
      <div className="channel-view-body">
        <div className="channel-view-main">
      {!isDm && (
        <div className="channel-view-tabs">
          <button
            className={`channel-view-tab${activeTab === 'chat' ? ' active' : ''}`}
            data-tab="chat"
            onClick={() => handleTabChange('chat')}
          >
            Chat
          </button>
          <button
            className={`channel-view-tab${activeTab === 'tasks' ? ' active' : ''}`}
            data-tab="tasks"
            onClick={() => handleTabChange('tasks')}
          >
            Tasks
          </button>
          <button
            className={`channel-view-tab${activeTab === 'files' ? ' active' : ''}`}
            data-tab="files"
            onClick={() => handleTabChange('files')}
          >
            Files
          </button>
          <button
            className={`channel-view-tab${activeTab === 'insights' ? ' active' : ''}`}
            data-tab="insights"
            onClick={() => handleTabChange('insights')}
          >
            Insights
          </button>
        </div>
      )}
      {activeTab === 'tasks' && !isDm ? (
        <TasksPanel channelId={channelId} embedded readOnly={isReadOnlyPublicChannel} onTaskClick={onTaskClick} />
      ) : activeTab === 'insights' && !isDm ? (
        <InsightsPanel guildId={guildId!} channelId={channelId} embedded onTaskClick={onTaskClick} />
      ) : activeTab === 'files' && !isDm ? (
        <FilesPanel channelId={channelId} readOnly={isReadOnlyPublicChannel} />
      ) : (
        <>
          <ConnectionStatus state={connectionState} />
          <MessageList channelId={channelId} onThreadOpen={onThreadOpen} activeThreadMessageId={activeThreadMessageId} emptyTitle={isDm ? dmChannel.peer.display_name : undefined} isDirectMessage={isDm} directMessagePeer={isDm ? dmChannel.peer : undefined} onStarterPrompt={setStarterPrompt} />
          <>
            <ActivityBar channelId={channelId} />
            {isReadOnlyPublicChannel ? (
              <div className="readonly-composer-placeholder" role="status">
                <LockOutlined aria-hidden="true" />
                <span>Read-only</span>
                <button onClick={handleJoin} disabled={joining}>
                  {joining ? 'Joining...' : `Join #${channelDisplayName(channel!)} to reply`}
                </button>
              </div>
            ) : (
              <MessageInput channelId={channelId} draftKey={channelDraftKey(channelId)} suggestedText={starterPrompt} onSuggestedTextApplied={() => setStarterPrompt(undefined)} disabled={composerDisabled} />
            )}
          </>
        </>
      )}
        </div>
        {showMembers && channel && (
          <ServerMembersPanel channelId={channel.id} />
        )}
      </div>
      {showSettings && channel && (
        <ChannelSettingsModal
          channelId={channel.id}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
